'use client';

// The shop's own product labels (Προσφορά, BEST SELLER, Χωρίς Γλουτένη…) as
// coloured labels (founder, 2026-10-09; option A under the photo was tried and
// dropped the same day — uneven card layouts). Only rendered when the client turns
// `ui.showProductTags` on; the rules live in lib/productTags.ts.
//
// Grid cards: stacked on the photo's top-left corner (option B) so every card
// keeps the same fixed layout. List rows: a wrapping row under the price.

import React from 'react';
import { sortedTextTags, imageTags, upperGreek, readableTextColor, type ProductTag } from '@/lib/productTags';

function Chip({ tag }: { tag: ProductTag }) {
  return (
    <span
      className="flex-none inline-flex items-center justify-center h-[26px] max-w-[96px] px-[7px] text-[9px] font-gotham font-bold leading-[1.05] text-center tracking-[0.02em]"
      style={{ backgroundColor: tag.bg || '#6B6B6B', color: readableTextColor(tag.bg, tag.color) }}
    >
      {upperGreek(tag.label)}
    </span>
  );
}

export function ProductTagWrap({ tags, className = '' }: { tags: ProductTag[] | undefined; className?: string }) {
  const sorted = sortedTextTags(tags);
  if (!sorted.length) return null;
  return (
    <div className={`flex flex-wrap ${className}`}>
      {sorted.map((t) => (
        <Chip key={t.label} tag={t} />
      ))}
    </div>
  );
}

/**
 * Grid cards, option A «under the photo» (demo switcher, founder 2026-10-21):
 * a strip of EXACTLY two rows, reserved even when a product has no labels, so
 * every card is the same height and the brand, title, price and button sit at
 * the same place on every card. When the labels need a third row (narrow
 * cards), the strip shows as many as fit plus a grey «+N».
 */
// Layout effect in the browser, plain effect on the server (no SSR warning).
const useIsoLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export const TAG_STRIP_HEIGHT = 56; // 2 rows × 26px + 4px between

export function ProductTagStrip({ tags, className = '' }: { tags: ProductTag[] | undefined; className?: string }) {
  const sorted = sortedTextTags(tags);
  const boxRef = React.useRef<HTMLDivElement>(null);
  const key = sorted.map((t) => t.label).join('|');
  const [shown, setShown] = React.useState(sorted.length);
  // Bumped when the card width changes: start again from "all" and re-measure.
  const [pass, setPass] = React.useState(0);
  const [lastKey, setLastKey] = React.useState(key);
  if (key !== lastKey) {
    setLastKey(key);
    setShown(sorted.length);
  }

  React.useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let width = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== width) {
        width = el.clientWidth;
        setShown(sorted.length);
        setPass((n) => n + 1);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Drop one label at a time (making room for «+N») until two rows hold them.
  useIsoLayoutEffect(() => {
    const el = boxRef.current;
    if (el && shown > 1 && el.scrollHeight > TAG_STRIP_HEIGHT + 1) setShown((n) => n - 1);
  }, [shown, key, pass]);

  const visible = sorted.slice(0, shown);
  const rest = sorted.slice(shown);
  return (
    <div
      ref={boxRef}
      className={`flex flex-wrap content-start gap-y-[4px] overflow-hidden ${className}`}
      style={{ height: TAG_STRIP_HEIGHT }}
    >
      {visible.map((t) => (
        <Chip key={t.label} tag={t} />
      ))}
      {rest.length > 0 && (
        <span
          className="flex-none inline-flex items-center justify-center h-[26px] px-[7px] text-[9px] font-gotham font-bold bg-[#EDEDED] text-[#333333]"
          title={rest.map((t) => upperGreek(t.label)).join(' · ')}
        >
          +{rest.length}
        </span>
      )}
    </div>
  );
}

/**
 * Grid cards (option B, founder 2026-10-09): the labels stacked down the photo's
 * top-left corner, so the card keeps its fixed size and every card in a row is
 * laid out identically. All labels show: bazaar's feed has at most 5, and five
 * 17px pills take ~97px of the 188px photo. Safety net only, in case a feed
 * ever carries more: past MAX_ON_PHOTO the stack shows MAX_ON_PHOTO − 1 plus a
 * grey «+N» (never triggers on today's data).
 */
const MAX_ON_PHOTO = 6;

export function ProductTagStack({ tags }: { tags: ProductTag[] | undefined }) {
  const sorted = sortedTextTags(tags);
  if (!sorted.length) return null;
  const overflow = sorted.length > MAX_ON_PHOTO;
  const shown = overflow ? sorted.slice(0, MAX_ON_PHOTO - 1) : sorted;
  const rest = overflow ? sorted.slice(MAX_ON_PHOTO - 1) : [];
  const pill = 'inline-flex items-center h-[17px] px-[6px] rounded-[3px] text-[8.5px] font-gotham font-bold tracking-[0.02em] whitespace-nowrap shadow-[0_1px_2px_rgba(0,0,0,0.12)]';
  return (
    <div className="absolute top-0 left-0 z-10 flex flex-col items-start gap-[3px] pointer-events-none">
      {shown.map((t) => (
        <span key={t.label} className={pill} style={{ backgroundColor: t.bg || '#6B6B6B', color: readableTextColor(t.bg, t.color) }}>
          {upperGreek(t.label)}
        </span>
      ))}
      {rest.length > 0 && (
        <span className={`${pill} bg-[#EDEDED] text-[#333333]`} title={rest.map((t) => upperGreek(t.label)).join(' · ')}>
          +{rest.length}
        </span>
      )}
    </div>
  );
}

/** Picture labels (Μείωση Τιμής, Μόνο στα Bazaar) in the photo's bottom-right corner. */
export function ProductTagStickers({ tags, size = 46 }: { tags: ProductTag[] | undefined; size?: number }) {
  const stickers = imageTags(tags).slice(0, 2);
  if (!stickers.length) return null;
  return (
    <div className="absolute right-[-6px] bottom-0 z-10 flex gap-[4px] pointer-events-none">
      {stickers.map((t) => (
        <img
          key={t.label}
          src={t.image}
          alt={t.label}
          className="object-contain"
          style={{ width: size, height: size }}
          loading="lazy"
        />
      ))}
    </div>
  );
}
